import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import { ChevronLeftIcon, FriendsIcon, SearchIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Button,
	Container,
	Empty,
	Heading3,
	Jumbo,
	Label,
	Option,
	Paragraph,
	PlainCard,
	Popover,
	Select,
	Sidebar,
	SidebarLayout,
	Skeleton,
	Subheading,
	Tabs,
} from '@treams/treams-ui';

import { useManagerMode } from '../navigation/ManagerModeContext';
import CompetencyDisplay from '../packages/kakapo-ui-application/common/containers/CompetencyDisplay';

function CompetencySearchSidebar() {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const history = useHistory();
	const managerMode = useManagerMode();
	const { data } = graphql.useCompetenciesQuery({ variables: { isArchived: false, first: 1000 } });

	return (
		<Sidebar>
			{!managerMode.enabled && (
				<Button
					title={t('sidebar.back.skills')}
					type="secondary"
					icon={<ChevronLeftIcon />}
					link={
						history.location.pathname.includes('growth-path') ? '/growth-path' : '/competencies'
					}
					mb={4}
				/>
			)}

			<Label mb={2}>{t('competencies.search.sidebar.browse')}</Label>
			{!data && (
				<Skeleton.Repeat count={3}>
					<Sidebar.Item.Skeleton small />
				</Skeleton.Repeat>
			)}
			{data &&
				_.sortBy(data.competencies.edges, (competency) => {
					return localize(competency.node.title).text;
				}).map(({ node }) => (
					<Sidebar.Item
						key={node.id}
						title={localize(node.title).text}
						path={`/competencies/search/${node.id}`}
						small
					/>
				))}
		</Sidebar>
	);
}

function CompetencySearchTabs() {
	const { t } = useTranslation();

	return (
		<Tabs.Container>
			<Tabs.Item title={t('competencies.search.start.tab.colleagues')} selected />
		</Tabs.Container>
	);
}

export function CompetencySearchStart() {
	const { t } = useTranslation();

	return (
		<SidebarLayout sidebar={<CompetencySearchSidebar />}>
			<Jumbo
				title={t('competencies.search.start.title')}
				description={t('competencies.search.list.description')}
				footer={<CompetencySearchTabs />}
			/>

			<Container my={3}>
				<Skeleton.Empty
					contents={
						<Empty
							icon={<FriendsIcon />}
							title={t('competencies.search.list.start.title')}
							description={t('competencies.search.list.start.description')}
						/>
					}
				>
					<Skeleton.Repeat count={3}>
						<CompetencySearchListItem.Skeleton />
					</Skeleton.Repeat>
				</Skeleton.Empty>
			</Container>
		</SidebarLayout>
	);
}

function CompetencySearchListItem(props: {
	appraisal: graphql.CompetencyAppraisal;
	levels: graphql.CompetencyLevelOption[];
	onClick: () => void;
}) {
	const { localize } = useLocalize();
	const { user } = props.appraisal;

	const levelIndex = props.levels.findLastIndex(
		(level, index) => level.enabled && index <= props.appraisal.competencyLevelIndex,
	);

	return (
		<PlainCard onClick={props.onClick}>
			<Flex py={3} alignItems="center">
				<Avatar size={32} user={user} mr={3} />
				<Box flexGrow={1} flexShrink={1}>
					<Heading3>
						{user.firstName} {user.lastName}
					</Heading3>
					<Paragraph>
						{user.jobPositions
							.map((jobPosition) => {
								return localize(jobPosition.jobTitle.title).text;
							})
							.join(', ')}
					</Paragraph>
				</Box>
				<CompetencyDisplay levels={props.levels} employee={levelIndex} labels={false} />
			</Flex>
		</PlainCard>
	);
}

CompetencySearchListItem.Skeleton = function CompetencySearchListItemSkeleton() {
	return (
		<PlainCard.Skeleton>
			<Flex py={3} alignItems="center">
				<Avatar.Skeleton size={32} mr={3} />
				<Box flexGrow={1} flexShrink={1}>
					<Heading3.Skeleton mb={2} />
					<Paragraph.Skeleton lines={1} />
				</Box>
			</Flex>
		</PlainCard.Skeleton>
	);
};

export default function CompetencySearch() {
	const { params } = useRouteMatch<{ id: string }>();
	const { localize } = useLocalize();

	const { t } = useTranslation();
	const { data } = graphql.useCompetencyAppraisalsQuery({ variables: { id: params.id } });

	const [visiblePopoverID, setVisiblePopoverID] = useState<undefined | string>();
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

	// Sort by competency scores descending.
	let appraisals = _.sortBy(data?.competency.appraisals, (appraisal) => {
		return appraisal.competencyLevelIndex;
	});
	appraisals = sortOrder === 'desc' ? appraisals.reverse() : appraisals;

	const sortingOptions: Option[] = [
		{
			title: t('competencies.search.list.sort.desc'),
			value: 'desc',
		},
		{
			title: t('competencies.search.list.sort.asc'),
			value: 'asc',
		},
	];

	return (
		<SidebarLayout sidebar={<CompetencySearchSidebar />}>
			<Jumbo
				title={
					data &&
					t('competencies.search.list.title', { skill: localize(data?.competency.title).text })
				}
				description={t('competencies.search.list.description')}
				footer={<CompetencySearchTabs />}
			/>

			<Container my={3}>
				<Flex mb={3}>
					<Select
						placeholder={t('competencies.search.list.sort.title')}
						search={false}
						options={sortingOptions}
						selectedOption={sortingOptions.find(({ value }) => sortOrder === value)}
						onChange={(value) => setSortOrder(value as 'asc' | 'desc')}
						disabled={appraisals.length === 0}
					/>
				</Flex>

				{!data && (
					<Skeleton.Repeat count={3}>
						<CompetencySearchListItem.Skeleton />
					</Skeleton.Repeat>
				)}

				{data && appraisals.length === 0 && (
					<Skeleton.Empty
						contents={
							<Empty
								icon={<SearchIcon />}
								title={t('competencies.search.list.empty.title')}
								description={t('competencies.search.list.empty.description')}
							/>
						}
					>
						<Skeleton.Repeat count={3}>
							<CompetencySearchListItem.Skeleton />
						</Skeleton.Repeat>
					</Skeleton.Empty>
				)}

				{data &&
					appraisals.map((appraisal) => (
						<Popover.Target
							key={appraisal.user.id}
							visible={visiblePopoverID === appraisal.user.id}
							contents={
								<Box p={3} minWidth={280}>
									<Subheading>
										{appraisal.user.firstName} {appraisal.user.lastName}
									</Subheading>
									<Paragraph mb={2}>{appraisal.user.email}</Paragraph>
									<Button
										link={`/users/${appraisal.user.id}`}
										title={t('competencies.search.list.actions.viewProfile')}
										type="secondary"
										small
										mb={2}
									/>
									<Button
										title={t('competencies.search.list.actions.copyEmail')}
										type="secondary"
										small
										onClick={() => {
											navigator.clipboard.writeText(appraisal.user.email);
										}}
									/>
								</Box>
							}
							onDismiss={() => setVisiblePopoverID(undefined)}
						>
							<CompetencySearchListItem
								onClick={() => {
									setVisiblePopoverID(appraisal.user.id);
								}}
								levels={data.competency.levels}
								appraisal={appraisal as graphql.CompetencyAppraisal}
							/>
						</Popover.Target>
					))}
			</Container>
		</SidebarLayout>
	);
}
